import usePrivateLink from "../../config/usePrivateLink";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import * as XLSX from "xlsx";

import { GetDueFeeReport } from "../../../api/ReportsServices";

import { getBatchList } from "../../../api/BatchService";
import {
  formatDate,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../../master/Master";
import { getSessions } from "../../../api/SessionsService";
import { setPrintContent } from "../../../PrintStore";
import ReportLayout from "./ReportLayout";

export default function FeeDue() {
  const hasAccess = usePrivateLink();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [Data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSessions] = useState("");
  const [batchId, setBatchID] = useState("");
  const [name, setName] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [error, setError] = useState("");
  const getSession = async () => {
    setLoading(true);
    try {
      const response = await getSessions();
      if (response?.data?.status) {
        const sessionList = response?.data?.data || [];
        setSessions(sessionList);
        const activeSession = sessionList.find(
          (session) => String(session.status) === "1",
        );
        if (activeSession) {
          setSelectedSessions(activeSession.id);
        } else if (sessionList.length > 0) {
          setSelectedSessions(sessionList[0].id);
        }
      } else {
        setError("Failed to fetch session.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);
  const getBatches = async () => {
    if (!selectedSession) return;

    try {
      const addUrl = `&session_id=${selectedSession}`;

      const response = await getBatchList(addUrl);

      setBatches(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch batches:", error);

      setBatches([]);
    }
  };

  useEffect(() => {
    getBatches();
  }, [selectedSession]);

  const buildQuery = (currentPage = page) => {
    let addUrl = `&page=${currentPage}&session_id=${selectedSession}`;

    if (minAmount !== "") {
      addUrl += `&min_amount=${minAmount}`;
    }

    if (maxAmount !== "") {
      addUrl += `&max_amount=${maxAmount}`;
    }

    if (batchId) {
      addUrl += `&batch_id=${batchId}`;
    }

    if (name.trim()) {
      addUrl += `&search=${encodeURIComponent(name.trim())}`;
    }

    return addUrl;
  };

  const fetchData = async () => {
    if (!selectedSession) return;

    setLoading(true);

    try {
      const response = await GetDueFeeReport(buildQuery(page));

      setData(response?.data?.data || null);
      setTotal(response?.data?.total_sum || 0);
    } catch (error) {
      console.error("Failed to fetch due fee report:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch due fee report.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, minAmount, maxAmount, batchId, name, selectedSession]);

  const calculateFee = (student) => {
    const finalAmount =
      Number(student?.total_fee ?? 0) +
      Number(student?.bus_final_fees ?? 0) +
      Number(student?.reg_fee ?? 0);

    const paidAmount =
      Number(student?.fees_paid ?? 0) +
      Number(student?.bus_paid_fees ?? 0) +
      Number(student?.paid_reg_fee ?? 0);

    const pendingAmount = finalAmount - paidAmount;

    return {
      finalAmount,
      paidAmount,
      pendingAmount,
    };
  };

  const pageSummary = useMemo(() => {
    const records = Data?.data || [];

    let finalAmount = 0;
    let paidAmount = 0;
    let pendingAmount = 0;

    records.forEach((student) => {
      const fee = calculateFee(student);

      finalAmount += fee.finalAmount;
      paidAmount += fee.paidAmount;
      pendingAmount += fee.pendingAmount;
    });

    return {
      finalAmount,
      paidAmount,
      pendingAmount,
      students: records.length,
    };
  }, [Data]);

  const resetFilters = () => {
    setBatchID("");
    setName("");
    setMinAmount("");
    setMaxAmount("");
    setPage(1);
  };

  const hasFilters = batchId || name || minAmount || maxAmount;

  const handleExportAll = async () => {
    try {
      setLoading(true);

      const baseQuery = buildQuery(1);

      const firstResponse = await GetDueFeeReport(baseQuery);

      let allData = Array.isArray(firstResponse?.data?.data?.data)
        ? [...firstResponse.data.data.data]
        : [];

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      for (let currentPage = 2; currentPage <= lastPage; currentPage++) {
        const response = await GetDueFeeReport(buildQuery(currentPage));

        const records = response?.data?.data?.data;

        if (Array.isArray(records)) {
          allData.push(...records);
        }
      }

      if (!allData.length) {
        SweetAlertShow(
          "info",
          "No Data",
          "No due fee records found to export.",
        );

        return;
      }

      const formattedData = allData.map((item, index) => {
        const student = item?.student_info || {};

        const course = item?.course || {};

        const batch = item?.batch || {};

        const fee = calculateFee(item);

        return {
          "SR.No": index + 1,

          "Student Name": student.name || "",

          "Father Name": student.father_name || "",

          "Registration No": student.registration_no || "",

          "Mobile Number": student.phone || "",

          Category: student.category || "",

          Gender: student.gender || "",

          DOB: student.dob || "",

          State: student.state || "",

          District: student.district || "",

          Village: student.village || "",

          Pincode: student.zipcode || "",

          Course: course.title || "",

          Batch: batch.title || "",

          "Admission Date": item.admission_date || "",
          "Main Fee": item.batch_fees || 0,
          "Final Amount": fee.finalAmount,
          "Total Fee": item.total_fee || 0,
          "Reg. Fee": item.reg_fee || 0,
          "Paid Reg. Fee": item.paid_reg_fee || 0,
          "Paid Fee": item.fees_paid || 0,

          "Paid Amount": fee.paidAmount,

          "Left Amount": fee.pendingAmount,

          "Batch Discount": item.batch_discount || 0,
          "Next Payment Date": item.next_payment_date || "",

          "RTE Type": item.rte || "",
        };
      });

      const workbook = XLSX.utils.book_new();

      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // Auto column widths
      const columnWidths = Object.keys(formattedData[0] || {}).map((key) => {
        const maxLength = Math.max(
          key.length,
          ...formattedData
            .slice(0, 100)
            .map((row) => String(row[key] ?? "").length),
        );

        return {
          wch: Math.min(Math.max(maxLength + 2, 12), 35),
        };
      });

      worksheet["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Due Fee Report");

      XLSX.writeFile(workbook, "DueFeeReport.xlsx");

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} student records successfully!`,
      });
    } catch (error) {
      console.error(error);

      SweetAlertShow(
        "error",
        "Export Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to export report.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!Data?.data?.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no records available for printing.",
      );

      return;
    }

    const printTable = (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <caption className="text-center fw-bold fs-5 mb-2">
              Due Fee List
            </caption>
            <tr className="text-center">
              <th>#</th>
              <th>Student Name</th>
              <th>Mobile</th>
              <th>Batch</th>
              <th>Final Amount</th>
              <th>Paid Amount</th>
              <th>Pending Amount</th>
              <th>Admission Date</th>
            </tr>
          </thead>
          <tbody>
            {Data?.data?.map((student, index) => {
              const fee = calculateFee(student);

              return (
                <tr key={student?.id || index} className="text-center">
                  <td>{index + 1}</td>

                  <td>
                    {student?.student_info?.name || "N/A"}

                    <br />

                    <small>{student?.student_info?.registration_no}</small>
                  </td>

                  <td>{student?.student_info?.phone}</td>

                  <td>{student?.batch?.title}</td>

                  <td>₹{fee.finalAmount.toLocaleString("en-IN")}</td>

                  <td>₹{fee.paidAmount.toLocaleString("en-IN")}</td>

                  <td>₹{fee.pendingAmount.toLocaleString("en-IN")}</td>

                  <td>{formatDate(student?.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

    setPrintContent(printTable);
    navigate("/print");
  };
  const handlePageChange = (button, index) => {
    if (!button?.url) return;

    const isFirst = index === 0;

    const isLast = index === Data?.links?.length - 1;

    if (isFirst) {
      setPage(Math.max(1, Number(Data?.current_page) - 1));
    } else if (isLast) {
      setPage(Number(Data?.current_page) + 1);
    } else {
      setPage(Number(button.label));
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-3">
                {/* =================================================
                    REPORT NAVIGATION
                ================================================== */}

                <div className="mb-3">
                  <ReportLayout />
                </div>

                {/* =================================================
                    PAGE HEADER
                ================================================== */}

                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
                      {/* TITLE */}

                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white flex-shrink-0"
                          style={{
                            width: "48px",
                            height: "48px",
                          }}
                        >
                          <i className="ri-money-rupee-circle-line fs-3"></i>
                        </div>

                        <div>
                          <h4 className="mb-1 fw-bold">Fee Due Report</h4>

                          <small className="text-muted">
                            Track outstanding student fee balances
                          </small>
                        </div>
                      </div>

                      {/* SESSION */}

                      <div
                        className="form-floating form-floating-outline"
                        style={{
                          minWidth: "230px",
                        }}
                      >
                        <select
                          className="form-select"
                          value={selectedSession}
                          onChange={(e) => {
                            setSelectedSessions(e.target.value);
                            setPage(1);
                          }}
                        >
                          {sessions?.map((session) => (
                            <option key={session?.id} value={session?.id}>
                              {session?.session}

                              {String(session?.status) === "1"
                                ? " • Active"
                                : " • Inactive"}
                            </option>
                          ))}
                        </select>

                        <label>Academic Session</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    FILTER CARD
                ================================================== */}

                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                      <div>
                        <h6 className="mb-1 fw-bold">
                          <i className="ri-filter-3-line text-primary me-2"></i>
                          Report Filters
                        </h6>

                        <small className="text-muted">
                          Filter students by batch, name, registration number or
                          pending amount.
                        </small>
                      </div>

                      {hasFilters ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={resetFilters}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset Filters
                        </button>
                      ) : null}
                    </div>

                    <div className="row g-3">
                      {/* BATCH */}

                      <div className="col-12 col-md-4">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={batchId}
                            onChange={(e) => {
                              setBatchID(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Batches</option>

                            {batches?.map((batch) => {
                              const courseTitle = batch?.course?.title || "";

                              const shortCourse =
                                courseTitle.length > 25
                                  ? `${courseTitle.substring(0, 25)}...`
                                  : courseTitle;

                              return (
                                <option key={batch?.id} value={batch?.id}>
                                  {batch?.title}
                                  {shortCourse ? ` (${shortCourse})` : ""}
                                </option>
                              );
                            })}
                          </select>

                          <label>Select Batch</label>
                        </div>
                      </div>

                      {/* SEARCH */}

                      <div className="col-12 col-md-4">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>Student Name / Mobile / Registration No</label>
                        </div>
                      </div>

                      {/* MIN AMOUNT */}

                      <div className="col-6 col-md-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            placeholder="Min"
                            value={minAmount}
                            onChange={(e) => {
                              setMinAmount(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>Min Due ₹</label>
                        </div>
                      </div>

                      {/* MAX AMOUNT */}

                      <div className="col-6 col-md-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            placeholder="Max"
                            value={maxAmount}
                            onChange={(e) => {
                              setMaxAmount(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>Max Due ₹</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    KPI CARDS
                ================================================== */}

                <div className="row g-3 mb-3">
                  {/* TOTAL DUE */}

                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Total Pending
                            </small>

                            <h4 className="fw-bold mb-1 text-danger">
                              ₹
                              {Number(
                                total || pageSummary.pendingAmount || 0,
                              ).toLocaleString("en-IN")}
                            </h4>

                            <small className="text-muted">
                              Outstanding fee
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-danger"
                            style={{
                              width: "46px",
                              height: "46px",
                            }}
                          >
                            <i className="ri-wallet-3-line fs-4 text-danger"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STUDENTS */}

                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Students With Due
                            </small>

                            <h4 className="fw-bold mb-1">{Data?.total || 0}</h4>

                            <small className="text-muted">Students found</small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                            style={{
                              width: "46px",
                              height: "46px",
                            }}
                          >
                            <i className="ri-group-line fs-4 text-primary"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AVERAGE */}

                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Average Pending
                            </small>

                            <h4 className="fw-bold mb-1">
                              ₹
                              {Data?.total
                                ? Number(
                                    Number(total || 0) / Number(Data.total),
                                  ).toLocaleString("en-IN", {
                                    maximumFractionDigits: 0,
                                  })
                                : "0"}
                            </h4>

                            <small className="text-muted">Per student</small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-warning"
                            style={{
                              width: "46px",
                              height: "46px",
                            }}
                          >
                            <i className="ri-bar-chart-2-line fs-4 text-warning"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    REPORT TABLE
                ================================================== */}

                <div className="card border-0 shadow-sm">
                  {/* TOOLBAR */}

                  <div className="card-header border-bottom p-3">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                      <div>
                        <h6 className="mb-1 fw-bold">Due Fee Students</h6>

                        <small className="text-muted">
                          Students with outstanding fee balances
                        </small>
                      </div>

                      <div className="d-flex gap-2">
                        {hasAccess("/student/due/fee/print") && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line me-1"></i>
                            Print
                          </button>
                        )}

                        {hasAccess("/student/due/fee/export") && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleExportAll}
                          >
                            <i className="ri-file-excel-2-line me-1"></i>
                            Export Excel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TABLE */}

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th
                              className="ps-4"
                              style={{
                                width: "65px",
                              }}
                            >
                              #
                            </th>

                            <th>Student</th>

                            <th>Mobile</th>

                            <th>Batch</th>

                            <th className="text-end">Final Fee</th>

                            <th className="text-end">Paid</th>

                            <th className="text-end">Pending</th>

                            <th>Admission</th>
                          </tr>
                        </thead>

                        <tbody>
                          {Data?.data?.length > 0 ? (
                            Data.data.map((student, index) => {
                              const fee = calculateFee(student);

                              const studentName =
                                student?.student_info?.name ||
                                "Unknown Student";

                              const registrationNo =
                                student?.student_info?.registration_no || "N/A";

                              const mobile =
                                student?.student_info?.phone || "N/A";

                              const firstLetter = studentName
                                .charAt(0)
                                .toUpperCase();

                              return (
                                <tr key={student?.id || index}>
                                  {/* NUMBER */}

                                  <td className="ps-4">
                                    <span className="text-muted fw-medium">
                                      {(Data?.from || 1) + index}
                                    </span>
                                  </td>

                                  {/* STUDENT */}

                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                        }}
                                      >
                                        <span className="fw-bold text-primary">
                                          {firstLetter}
                                        </span>
                                      </div>

                                      <div>
                                        <div className="fw-semibold">
                                          {studentName}
                                        </div>

                                        <small className="text-muted">
                                          {registrationNo}
                                        </small>
                                      </div>
                                    </div>
                                  </td>

                                  {/* MOBILE */}

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="ri-phone-line text-muted"></i>

                                      <span>{mobile}</span>
                                    </div>
                                  </td>

                                  {/* BATCH */}

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-2 bg-label-info d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                          width: "34px",
                                          height: "34px",
                                        }}
                                      >
                                        <i className="ri-bookmark-3-line text-info"></i>
                                      </div>

                                      <span className="fw-medium">
                                        {student?.batch?.title || "N/A"}
                                      </span>
                                    </div>
                                  </td>

                                  {/* FINAL */}

                                  <td className="text-end">
                                    <span className="fw-semibold">
                                      ₹{fee.finalAmount.toLocaleString("en-IN")}
                                    </span>
                                  </td>

                                  {/* PAID */}

                                  <td className="text-end">
                                    <span className="fw-semibold text-success">
                                      ₹{fee.paidAmount.toLocaleString("en-IN")}
                                    </span>
                                  </td>

                                  {/* PENDING */}

                                  <td className="text-end">
                                    <span className="badge bg-label-danger rounded-pill px-3 py-2">
                                      ₹
                                      {fee.pendingAmount.toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                  </td>

                                  {/* ADMISSION */}

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="ri-calendar-line text-muted"></i>

                                      <span>
                                        {formatDate(student?.created_at)}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-5">
                                <div className="mb-3">
                                  <div
                                    className="rounded-circle bg-label-secondary d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      width: "70px",
                                      height: "70px",
                                    }}
                                  >
                                    <i className="ri-user-search-line fs-2 text-muted"></i>
                                  </div>
                                </div>

                                <h6 className="mb-1">
                                  No due fee students found
                                </h6>

                                <small className="text-muted">
                                  Try changing the batch or search filters.
                                </small>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* =================================================
                        PAGINATION
                    ================================================== */}

                    {Data?.total > 0 && (
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3 border-top">
                        <small className="text-muted">
                          Showing <strong>{Data?.from}</strong> to{" "}
                          <strong>{Data?.to}</strong> of{" "}
                          <strong>{Data?.total}</strong> students
                        </small>

                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            {Data?.links?.map((btn, index) => {
                              const isFirst = index === 0;

                              const isLast = index === Data.links.length - 1;

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    btn?.active ? "active" : ""
                                  } ${!btn?.url ? "disabled" : ""}`}
                                >
                                  <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => handlePageChange(btn, index)}
                                  >
                                    {isFirst ? (
                                      <i className="ri-arrow-left-s-line"></i>
                                    ) : isLast ? (
                                      <i className="ri-arrow-right-s-line"></i>
                                    ) : (
                                      btn.label
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </nav>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>
        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
}
